/**
 * HUD particle effects — turn indicator, aim sweep, knockback indicator.
 */

import { TURN_INDICATOR, AIM_FX } from '../../constants';
import { configParticle, type ParticleCore } from './types';

/**
 * Turn indicator burst — Sakurai-style choreographed multi-phase effect.
 *
 * Phase 0 (instant):  Impact flash + 8-arm star burst (crisp geometry)
 * Phase 1 (instant):  Converging arrows ▽ + 3-layer shockwaves
 * Phase 2 (instant):  Upward spark fan + radial speed lines + micro-particles
 * Phase 3 (+40ms):    Secondary ring + cluster billboards + ascending embers
 * Phase 4 (+80ms):    End flash pop (Sakurai Phase 4 punctuation) + smoke wisps
 *
 * All particles freeze-immune — animates through 150ms freeze-until gate.
 * Total: ~65 particles + 19 lines + 5 rings. Well within pool budget.
 */
export function spawnTurnIndicator(core: ParticleCore, x: number, y: number, color: number): void {
  const T = TURN_INDICATOR;

  // Helper: mark last N spawned particles as freeze-immune
  const markFreezeImmune = (count: number) => {
    let found = 0;
    for (let i = core.pool.length - 1; i >= 0 && found < count; i--) {
      if (core.pool[i].active && !core.pool[i].freezeImmune) {
        core.pool[i].freezeImmune = true;
        found++;
      }
    }
  };

  // ================================================================
  // PHASE 0: Impact flash + anime star burst (instant)
  // Sakurai: "A flash of light occurs, instantly flashing brightly"
  // Sakurai: "Mix in plenty of black" for any-background visibility
  // ================================================================

  // 8-arm impact star — crisp geometric lines (Sakurai: straight lines vs curves contrast)
  for (let i = 0; i < T.STAR_ARM_COUNT; i++) {
    const angle = (i / T.STAR_ARM_COUNT) * Math.PI * 2;
    const isCardinal = i % 2 === 0;
    core.impactLines.push({
      x, y, angle,
      innerRadius: T.STAR_INNER_RADIUS,
      outerRadius: T.STAR_OUTER_RADIUS * (isCardinal ? 1 : 0.65),
      expandSpeed: T.STAR_EXPAND_SPEED,
      color: isCardinal ? 0xffffff : color,
      lineWidth: isCardinal ? T.STAR_LINE_WIDTH : T.STAR_LINE_WIDTH * 0.7,
      life: T.STAR_LIFE,
      maxLife: T.STAR_LIFE,
      freezeImmune: true,
    });
  }

  // Bright center flash — large soft ADD (Sakurai: "duration quite long relative to explosion")
  for (let i = 0; i < T.FLASH_CENTER_COUNT; i++) {
    const flashColor = i === 0 ? 0xffffff : color;
    core.spawnParticle(
      x, y, flashColor,
      0, 0.5,
      T.FLASH_CENTER_LIFE, T.FLASH_CENTER_LIFE + 0.04,
      T.FLASH_CENTER_SCALE * (1 - i * 0.15), T.FLASH_CENTER_SCALE,
      0, 0.4,
      true, false,   // ADD, soft — bright wash
      0.85,
    );
  }
  markFreezeImmune(T.FLASH_CENTER_COUNT);

  // Dark contrast flash — Sakurai: "mixing in darker elements is effective"
  for (let i = 0; i < T.DARK_FLASH_COUNT; i++) {
    core.spawnParticle(
      x, y, T.DARK_COLOR,
      0, 0.3,
      T.DARK_FLASH_LIFE, T.DARK_FLASH_LIFE + 0.03,
      T.DARK_FLASH_SCALE, T.DARK_FLASH_SCALE + 0.5,
      0, 0.4,
      false, false,  // NORMAL, soft — dark hole behind bright flash
      0.65,
    );
  }
  markFreezeImmune(T.DARK_FLASH_COUNT);

  // ================================================================
  // PHASE 1: Converging arrows + 3-layer shockwaves (instant)
  // ================================================================

  // Converging arrow lines — downward ▽ pointing at tank
  const downAngle = -Math.PI / 2;
  for (let i = -1; i <= 1; i++) {
    const spread = i * T.ARROW_SPREAD;
    core.impactLines.push({
      x, y,
      angle: downAngle + spread,
      innerRadius: T.ARROW_OUTER_RADIUS,
      outerRadius: T.ARROW_INNER_RADIUS,
      expandSpeed: T.ARROW_EXPAND_SPEED,
      color: i === 0 ? 0xffffff : color,
      lineWidth: i === 0 ? T.ARROW_CENTER_WIDTH : T.ARROW_SIDE_WIDTH,
      life: T.ARROW_LIFE,
      maxLife: T.ARROW_LIFE,
      freezeImmune: true,
    });
  }

  // 3-layer shockwaves — Sakurai: dark outline trailing bright ring
  core.spawnShockwave(x, y, color, T.SHOCKWAVE_RADIUS, T.SHOCKWAVE_SPEED, T.SHOCKWAVE_WIDTH, T.SHOCKWAVE_LIFE);
  core.shockwaves[core.shockwaves.length - 1].freezeImmune = true;
  core.spawnShockwave(x, y, T.DARK_COLOR, T.SHOCKWAVE_RADIUS * 1.1, T.SHOCKWAVE_SPEED * 0.85, T.SHOCKWAVE_WIDTH * 1.5, T.SHOCKWAVE_LIFE * 0.9);
  core.shockwaves[core.shockwaves.length - 1].freezeImmune = true;
  core.spawnShockwave(x, y, 0xffffff, T.SHOCKWAVE_RADIUS * 0.45, T.SHOCKWAVE_SPEED * 1.2, T.SHOCKWAVE_WIDTH * 0.8, T.SHOCKWAVE_LIFE * 0.6);
  core.shockwaves[core.shockwaves.length - 1].freezeImmune = true;

  // ================================================================
  // PHASE 2: Upward spark fan + radial speed lines + micro-particles
  // Sakurai: "offset spawn positions along travel direction"
  // Sakurai: "smaller is more" for micro-particles
  // ================================================================

  // Upward spark fan — player color + white, stretched, offset spawns
  for (let i = 0; i < T.SPARK_COUNT; i++) {
    const sparkAngle = -Math.PI / 2 + (Math.random() - 0.5) * 2 * T.SPARK_FAN_HALF_ANGLE;
    const speed = T.SPARK_SPEED_MIN + Math.random() * (T.SPARK_SPEED_MAX - T.SPARK_SPEED_MIN);
    const life = T.SPARK_LIFE_MIN + Math.random() * (T.SPARK_LIFE_MAX - T.SPARK_LIFE_MIN);
    const scale = T.SPARK_SCALE_MIN + Math.random() * (T.SPARK_SCALE_MAX - T.SPARK_SCALE_MIN);
    const r = Math.random();
    const sparkColor = r < 0.25 ? 0xffffff : r < 0.85 ? color : T.DARK_COLOR;  // 15% dark sparks
    const off = 4 + Math.random() * 6;  // offset spawn — reduce center overlap waste
    core.spawnParticleAtAngle(
      x + Math.cos(sparkAngle) * off,
      y + Math.sin(sparkAngle) * off,
      sparkAngle, speed,
      sparkColor,
      life, scale,
      sparkColor === T.DARK_COLOR ? 0 : T.SPARK_GRAVITY, 0.96,
      sparkColor !== T.DARK_COLOR,  // dark = NORMAL, bright = ADD
      true,
      sparkColor === T.DARK_COLOR ? 0.7 : 0.9,
      2.5 + Math.random(),  // stretch 2.5-3.5x — velocity-aligned streaks
    );
  }
  markFreezeImmune(T.SPARK_COUNT);

  // Radial speed lines — manga-style action lines (Sakurai: straight lines contrast)
  for (let i = 0; i < T.SPEED_LINE_COUNT; i++) {
    const angle = (i / T.SPEED_LINE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const dark = Math.random() < 0.25;  // 25% dark lines for contrast
    core.impactLines.push({
      x, y, angle,
      innerRadius: T.SPEED_LINE_INNER + Math.random() * 5,
      outerRadius: T.SPEED_LINE_OUTER + Math.random() * 10,
      expandSpeed: T.SPEED_LINE_EXPAND + Math.random() * 50,
      color: dark ? T.DARK_COLOR : (Math.random() < 0.5 ? 0xffffff : color),
      lineWidth: dark ? T.SPEED_LINE_WIDTH * 1.5 : T.SPEED_LINE_WIDTH,
      life: T.SPEED_LINE_LIFE,
      maxLife: T.SPEED_LINE_LIFE,
      freezeImmune: true,
    });
  }

  // Micro-particles — Sakurai: "smaller is more" for perceived density
  for (let i = 0; i < T.MICRO_COUNT; i++) {
    const microAngle = Math.random() * Math.PI * 2;
    const microSpeed = 2 + Math.random() * 6;
    const microColor = Math.random() < 0.6 ? color : 0xffffff;
    const off = 3 + Math.random() * 5;
    core.spawnParticleAtAngle(
      x + Math.cos(microAngle) * off,
      y + Math.sin(microAngle) * off,
      microAngle, microSpeed,
      microColor,
      0.08 + Math.random() * 0.1,  // very brief
      0.08 + Math.random() * 0.12, // tiny scale
      0.04,
      0.92,
      true, true,  // ADD, hard — crisp tiny dots
      0.75,
    );
  }
  markFreezeImmune(T.MICRO_COUNT);

  // Dark framing — Sakurai: "creates contrast and visual hierarchy"
  // Offset spawns — no center overlap waste
  for (let i = 0; i < T.DARK_COUNT; i++) {
    core.spawnParticle(
      x, y, T.DARK_COLOR,
      0.5, 2.5,
      T.DARK_LIFE_MIN, T.DARK_LIFE_MAX,
      T.DARK_SCALE_MIN, T.DARK_SCALE_MAX,
      0, 0.5,
      false, false,  // NORMAL, soft
      0.6,
      1,
      4,  // spawn offset — reduce center overlap
    );
  }
  markFreezeImmune(T.DARK_COUNT);

  // ================================================================
  // PHASE 3: Secondary burst (+40ms) — Sakurai stagger timing
  // "Shift the timing to reduce displayed count while maintaining density"
  // ================================================================
  core.pendingBlasts.push({
    timestamp: Date.now() + T.PHASE3_DELAY,
    callback: () => {
      // Secondary shockwave — smaller, player color
      core.spawnShockwave(x, y, color, T.SHOCKWAVE_RADIUS * 0.5, T.SHOCKWAVE_SPEED * 1.3, T.SHOCKWAVE_WIDTH * 0.6, T.SHOCKWAVE_LIFE * 0.5);
      core.shockwaves[core.shockwaves.length - 1].freezeImmune = true;

      // Cluster billboard particles — Sakurai: "one particle reads as many"
      for (let i = 0; i < T.CLUSTER_COUNT; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 5;
        const p = core.getInactive();
        if (!p) continue;

        const off = 5 + Math.random() * 5;
        configParticle(p, core, {
          x: x + Math.cos(angle) * off,
          y: y + Math.sin(angle) * off,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: Math.random() < 0.5 ? color : 0xffffff,
          life: 0.15 + Math.random() * 0.1,
          scale: 0.5 + Math.random() * 0.4,
          alpha: 0.8,
          gravity: 0.03,
          friction: 0.94,
          hard: false,
          freezeImmune: true,
        });
        p.sprite.texture = core.clusterTexture;
      }

      // Ascending embers — float upward, player color + warm accents
      for (let i = 0; i < T.EMBER_COUNT; i++) {
        const emberAngle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2;
        const r = Math.random();
        const emberColor = r < 0.5 ? color : r < 0.8 ? 0xffaa44 : 0xffffff;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 10,
          y,
          emberAngle,
          1.5 + Math.random() * 3,
          emberColor,
          0.4 + Math.random() * 0.4,
          0.2 + Math.random() * 0.4,
          -0.03 - Math.random() * 0.02,  // float up
          0.99,
          true, false,   // ADD, soft — warm glow
          0.8,
        );
      }
      markFreezeImmune(T.EMBER_COUNT);
    },
  });

  // ================================================================
  // PHASE 4: End flash (+80ms) — Sakurai Phase 4 punctuation
  // "A flash-like effect creating a quick, sudden disappearance"
  // ================================================================
  core.pendingBlasts.push({
    timestamp: Date.now() + T.PHASE4_DELAY,
    callback: () => {
      // End flash pop — brief bright punch to punctuate
      core.spawnParticle(
        x, y, 0xffffff,
        0, 0,
        T.END_FLASH_LIFE, T.END_FLASH_LIFE,
        T.END_FLASH_SCALE, T.END_FLASH_SCALE,
        0, 1.0,
        true, false,   // ADD, soft
        0.9,
      );
      markFreezeImmune(1);

      // Smoke wisps — Sakurai: "baton pass, increasing dominance of smoke"
      for (let i = 0; i < T.SMOKE_COUNT; i++) {
        const smokeAngle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6;
        const r = Math.random();
        const smokeColor = r < 0.4 ? 0x221a28 : r < 0.7 ? 0x1a1520 : 0x333344;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 8,
          y,
          smokeAngle,
          0.5 + Math.random() * 1.5,
          smokeColor,
          0.3 + Math.random() * 0.3,
          1.0 + Math.random() * 1.0,
          -0.01,         // gentle float up
          0.985,
          false, false,  // NORMAL, soft — dark smoke
          0.4,
        );
      }
      markFreezeImmune(T.SMOKE_COUNT);
    },
  });
}

/**
 * Aim sweep sparks — small particles tangent to turret tip during fast rotation.
 * Sakurai composition: bright + dark contrast, brief lifespan, slight gravity arc.
 */
export function spawnAimSweepParticles(
  core: ParticleCore,
  tipX: number, tipY: number, tangentAngle: number, playerColor: number, count: number, scale: number,
): void {
  for (let i = 0; i < count; i++) {
    const isDark = Math.random() < AIM_FX.SPARK_DARK_CHANCE;
    const color = isDark ? AIM_FX.SPARK_DARK_COLOR : (Math.random() < 0.4 ? 0xffffff : playerColor);
    const speed = AIM_FX.SPARK_SPEED_MIN + Math.random() * (AIM_FX.SPARK_SPEED_MAX - AIM_FX.SPARK_SPEED_MIN);
    const angle = tangentAngle + (Math.random() - 0.5) * 0.8;
    const life = AIM_FX.SPARK_LIFE / 60;
    const jx = (Math.random() - 0.5) * 6;
    const jy = (Math.random() - 0.5) * 6;
    core.spawnParticleAtAngle(
      tipX + jx, tipY + jy,
      angle, speed,
      color,
      life,
      scale,
      0.04,
      0.96,
      !isDark,
      true,
      isDark ? 0.7 : 0.9,
      1.5,
    );
  }
}

/** Knockback direction indicator — Smash-style directional streaks from hit player */
export function spawnKnockbackIndicator(
  core: ParticleCore,
  px: number, py: number, vx: number, vy: number, color: number,
): void {
  const angle = Math.atan2(vy, vx);
  const speed = Math.sqrt(vx * vx + vy * vy);
  if (speed < 0.5) return;

  // Main direction arrow — 3 converging lines pointing in knockback direction
  for (let i = -1; i <= 1; i++) {
    const spread = i * 0.15; // slight fan
    const life = 0.35;
    core.impactLines.push({
      x: px, y: py,
      angle: angle + spread,
      innerRadius: 15,
      outerRadius: 30 + speed * 2,
      expandSpeed: 60 + speed * 3,
      color: i === 0 ? 0xffffff : color,
      lineWidth: i === 0 ? 4 : 2,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }
}
